import React, { useCallback, useEffect } from 'react';
import { withBreadcrumbsContext } from '@adjust/dashboard-components';
import { connectWithStore } from '@react/Common/connectWithStore';
import {
  getUserPermissionsState,
  navigateTo,
  dontUpdateBreadcrumb,
} from '@redux/ducks/userPermissions/userPermissions';
import { UserPermissionsScreen } from '@redux/ducks/userPermissions/utils';
import { UserPermissionsPage } from './UserPermissionsPage';
import { AppListPage } from './AppList/AppListPage';
import { AppAccessPage } from './AppAccess/AppAccessPage';
import { TrackerAccessPage } from './TrackerAccess/TrackerAccess';
import { PartnerAccessPage } from './PartnerAccess/PartnerAccess';

export const NavigationComponent = ({
  screen,
  user,
  closeSidebar,
  navigateTo,
  breadcrumbs,
  pushBreadcrumb,
  setBreadcrumbs,
  currentApp,
  onSubmit,
}) => {
  const title = t('account-settings.edit-user-access.edit-user-title', {
    userName: user?.full_name || user?.email,
  });

  const triggerLastBreadcrumbNavigation = () => {
    breadcrumbs.pop().action();
  };

  const getBreadcrumbObject = useCallback(
    ({ screen, onClick }) =>
      ({
        [UserPermissionsScreen.APP_LIST]: {
          id: UserPermissionsScreen.APP_LIST,
          label: title,
          action: () => onClick(UserPermissionsScreen.MAIN),
        },
        [UserPermissionsScreen.APP_ACCESS]: {
          id: UserPermissionsScreen.APP_ACCESS,
          label: t('label.app-access'),
          action: () => onClick(UserPermissionsScreen.APP_LIST),
        },
        [UserPermissionsScreen.PARTNER_ACCESS]: {
          id: UserPermissionsScreen.PARTNER_ACCESS,
          label: currentApp?.name,
          action: () => onClick(UserPermissionsScreen.APP_ACCESS),
        },
        [UserPermissionsScreen.TRACKER_ACCESS]: {
          id: UserPermissionsScreen.TRACKER_ACCESS,
          label: currentApp?.name,
          action: () => onClick(UserPermissionsScreen.APP_ACCESS),
        },
      }[screen]),
    [currentApp, title],
  );

  const updateBreadcrumb = useCallback(
    (screen, breadcrumbs) => {
      if (screen === UserPermissionsScreen.MAIN) {
        setBreadcrumbs([]);
        return;
      }

      const index = breadcrumbs.findIndex(breadcrumb => breadcrumb.id === screen);
      if (index >= 0) {
        setBreadcrumbs(breadcrumbs.slice(0, index + 1));
        return;
      }

      const breadcrumbInfo = getBreadcrumbObject({ screen, onClick: navigateTo });

      pushBreadcrumb(breadcrumbInfo);
    },
    [setBreadcrumbs, pushBreadcrumb, navigateTo, getBreadcrumbObject],
  );

  useEffect(() => {
    updateBreadcrumb(screen, breadcrumbs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, updateBreadcrumb]);

  switch (screen) {
    case UserPermissionsScreen.APP_LIST:
      return <AppListPage onCancel={() => triggerLastBreadcrumbNavigation()} />;
    case UserPermissionsScreen.APP_ACCESS:
      return <AppAccessPage onCancel={() => triggerLastBreadcrumbNavigation()} />;
    case UserPermissionsScreen.TRACKER_ACCESS:
      return <TrackerAccessPage onCancel={() => triggerLastBreadcrumbNavigation()} />;
    case UserPermissionsScreen.PARTNER_ACCESS:
      return <PartnerAccessPage onCancel={() => triggerLastBreadcrumbNavigation()} />;
    case UserPermissionsScreen.MAIN:
    default:
      return <UserPermissionsPage onCancel={closeSidebar} onSubmit={onSubmit} />;
  }
};

const mapStateToProps = state => {
  const { screen, user, currentApp } = getUserPermissionsState(state);
  return { screen, user, currentApp };
};

const mapStateToDispatch = {
  navigateTo,
  dontUpdateBreadcrumb,
};

const mapContextToProps = context => ({
  breadcrumbs: context.breadcrumbs,
  setBreadcrumbs: context.setBreadcrumbs,
  pushBreadcrumb: context.pushBreadcrumb,
});

const NavigationComponentWithBreadcrumbs =
  withBreadcrumbsContext<any, 'breadcrumbs' | 'setBreadcrumbs' | 'pushBreadcrumb'>(mapContextToProps)(
    NavigationComponent,
  );

export const Navigation = connectWithStore(mapStateToProps, mapStateToDispatch)(NavigationComponentWithBreadcrumbs);
